import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { Download as DownloadIcon } from '@/constants/hardfin-standard-icons';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback } from 'react';

interface TemplateDownloadProps {
  onBack: () => void;
  onNext: () => void;
}

export function TemplateDownload({ onBack, onNext }: TemplateDownloadProps) {
  const { user } = useCurrentUser();
  const handleDownloadTemplate = useCallback(() => {
    const headers = [
      'Name',
      'Address',
      'Company',
      'Is inventory',
      'Is transient',
    ];

    const csvContent = headers.join(',') + '\n';

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `location_data_template_${DateTime.now().toFormat('yyyy-MM-dd_HHmm')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  return (
    <Card variant={'outlined'}>
      <CardContent>
        <Box
          sx={{
            px: 3,
            py: 2,
            backgroundColor: (theme) => theme.palette.grey.A100,
            borderRadius: '10px',
            marginBottom: '10px',
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            Template for location data
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            Required
          </Typography>
          <ul style={{ paddingLeft: '24px', marginTop: '8px' }}>
            <li>
              <Typography variant="body2">Name - text</Typography>
            </li>
          </ul>
          <Typography variant="subtitle2" gutterBottom>
            Available fields
          </Typography>
          <ul style={{ paddingLeft: '24px', marginTop: 0, marginBottom: 0 }}>
            <li>
              <Typography variant="body2">Address - text</Typography>
            </li>
            <li>
              <Typography variant="body2">
                Company - text (leave blank for <i>{user.organization.name}</i>{' '}
                locations)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">Is inventory - true/false</Typography>
            </li>
            <li>
              <Typography variant="body2">Is transient - true/false</Typography>
            </li>
          </ul>
        </Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Important information about the template</AlertTitle>
          <ul style={{ paddingLeft: '24px', marginTop: '8px' }}>
            <li>
              Any fields included in your upload will overwrite existing data,
              including empty cells
            </li>
            <li>
              You can delete columns for fields you don't need to update (only
              "Name" is required)
            </li>
            <li>
              For targeted updates, only include the specific names and fields
              you want to modify
            </li>
          </ul>
        </Alert>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button variant="outlined" onClick={onBack}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Download template
          </Button>
          <Button variant="contained" onClick={onNext}>
            Next
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
